import React, { useEffect, useState } from 'react';
import { ownerApi } from '../../lib/ownerApi';
import { Search, ChevronRight, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_BADGE = {
  active_paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  free: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  trial: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300',
  demo: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
  past_due: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
  cancelled: 'bg-stone-200 text-stone-600 dark:bg-slate-700 dark:text-stone-400',
  expired: 'bg-stone-200 text-stone-600 dark:bg-slate-700 dark:text-stone-400',
  internal: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
};

const HEALTH_DOT = {
  healthy: 'bg-emerald-500',
  warning: 'bg-amber-500',
  critical: 'bg-rose-500',
  unknown: 'bg-stone-300 dark:bg-slate-600',
};

export default function OwnerAccountsListPage() {
  const [search, setSearch] = useState('');
  const [accountType, setAccountType] = useState('');
  const [planKey, setPlanKey] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ownerApi.queryAccounts({
      search: search || undefined,
      account_type: accountType || undefined,
      plan_key: planKey || undefined,
      limit: 100,
    })
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) { setData({ items: [], total: 0 }); setLoading(false); } });
    return () => { cancelled = true; };
  }, [search, accountType, planKey]);

  return (
    <div className="space-y-4" data-testid="owner-accounts">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Accounts</h2>
        <span className="text-xs text-stone-500" data-testid="owner-accounts-count">
          {loading ? '…' : `${data?.total ?? 0} accounts`}
        </span>
      </div>

      <div className="rounded-2xl border border orka-border-subtle orka-surface-raised p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-stone-400" />
          <input
            type="text"
            placeholder="Search by email or name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 h-9 rounded-lg border border orka-border-subtle bg-stone-50 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
            data-testid="owner-accounts-search"
          />
        </div>
        <Filter className="w-3.5 h-3.5 text-stone-400" />
        <select
          value={accountType}
          onChange={e => setAccountType(e.target.value)}
          className="h-9 px-2 rounded-lg border border orka-border-subtle orka-surface-raised text-xs"
          data-testid="owner-accounts-filter-type"
        >
          <option value="">All types</option>
          <option value="personal">Personal</option>
          <option value="team">Team</option>
          <option value="business">Business</option>
          <option value="enterprise">Enterprise</option>
          <option value="demo">Demo</option>
          <option value="trial">Trial</option>
          <option value="internal_test">Internal test</option>
          <option value="testing_account">Testing account (free)</option>
        </select>
        <select
          value={planKey}
          onChange={e => setPlanKey(e.target.value)}
          className="h-9 px-2 rounded-lg border border orka-border-subtle orka-surface-raised text-xs"
          data-testid="owner-accounts-filter-plan"
        >
          <option value="">All plans</option>
          <option value="free_personal">Free Personal</option>
          <option value="pro_personal">Pro Personal</option>
          <option value="team">Team</option>
          <option value="business">Business</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>

      <div className="rounded-2xl border border orka-border-subtle orka-surface-raised overflow-hidden">
        <table className="w-full text-sm" data-testid="owner-accounts-table">
          <thead className="bg-stone-50 dark:bg-slate-800 text-[11px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Account</th>
              <th className="text-left px-3 py-2.5 font-semibold">Type</th>
              <th className="text-left px-3 py-2.5 font-semibold">Plan</th>
              <th className="text-left px-3 py-2.5 font-semibold">Status</th>
              <th className="text-left px-3 py-2.5 font-semibold">Health</th>
              <th className="text-right px-4 py-2.5 font-semibold">Created</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-stone-400 text-xs">Loading…</td></tr>
            ) : (data?.items || []).length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-stone-400 text-xs italic">
                {search ? 'No accounts match your search.' : 'No accounts found.'}
              </td></tr>
            ) : (data?.items || []).map(a => (
              <tr
                key={a.account_id}
                className="border-t border-stone-100 dark:border-slate-800 hover:bg-stone-50 dark:hover:bg-slate-800/50"
                data-testid={`owner-account-row-${a.account_id}`}
              >
                <td className="px-4 py-2.5">
                  <Link to={`/owner/accounts/${encodeURIComponent(a.account_id)}`} className="block">
                    <div className="font-medium orka-text-primary truncate max-w-[260px]">{a.display_name || '—'}</div>
                    <div className="text-[11px] text-stone-500 truncate max-w-[260px]">{a.primary_email || a.account_id}</div>
                  </Link>
                </td>
                <td className="px-3 py-2.5">
                  <span className="text-[11px] px-1.5 py-0.5 rounded orka-surface-subtle orka-text-secondary capitalize">{a.account_type}</span>
                </td>
                <td className="px-3 py-2.5 text-xs font-mono">{a.plan_key}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-full font-semibold ${STATUS_BADGE[a.commercial_status] || STATUS_BADGE.free}`}>
                    {a.commercial_status?.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${HEALTH_DOT[a.health?.status] || HEALTH_DOT.unknown}`} />
                    <span className="text-[11px] orka-text-secondary capitalize">{a.health?.status || 'unknown'}</span>
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right text-[11px] font-mono text-stone-500">{(a.created_at || '').slice(0, 10)}</td>
                <td className="text-stone-400">
                  <Link to={`/owner/accounts/${encodeURIComponent(a.account_id)}`} className="block px-2 py-1">
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
